import React, {
  forwardRef, memo, useCallback, useEffect, useId, useLayoutEffect, useRef, useState,
} from "react";

/**
 * ChainFlow OS · PremiumTextarea
 * ────────────────────────────────────────────────────────────
 * Sixth brand primitive — the multi-line companion to `PremiumInput`.
 * Sanctioned for operational notes, legal summaries, internal
 * comments, seller updates, recovery narratives, and any other
 * multi-line free-form value entry on ChainFlow OS.
 *
 * Locked v1 interaction grammar (mirrors PremiumInput):
 *  • Native `<textarea>` underneath — IME composition, autofill,
 *    paste, keyboard reliability all work first time.
 *  • Optional `autoResize` measures `scrollHeight` and grows the
 *    textarea up to `maxRows`, then engages internal scroll.
 *  • Optional `resize` prop — "none" / "vertical" / "both" (default
 *    "vertical"). `autoResize` forces "none".
 *  • Optional `maxLength` enforcement; with `showCount` a calm slate
 *    counter renders below right ("123 / 500"). Approaching limit
 *    (>90%) shifts colour to amber, at limit to rose.
 *  • Error / disabled / readOnly / required / loading states match
 *    PremiumInput exactly so adjacent fields feel cohesive.
 *
 * Locked v1 visual identity (matches PremiumInput):
 *  • Soft white surface, slate-200 border, slate-300 on hover.
 *  • Violet focus-visible ring (rose for error). 180 ms transitions.
 *  • Rounded-xl. Default min-height 88 px (~4 rows of operational
 *    notes); customisable via `rows` / `minRows`.
 *  • Padding tuned for operational note-taking — not chat-app
 *    oversized.
 *  • `prefers-reduced-motion` suppresses all transitions.
 *
 * @prop {string} value
 * @prop {(v) => void} onChange
 * @prop {string} [label]
 * @prop {string} [hint]
 * @prop {string} [error]
 * @prop {string} [placeholder]
 * @prop {boolean} [required=false]
 * @prop {boolean} [disabled=false]
 * @prop {boolean} [readOnly=false]
 * @prop {boolean} [loading=false]
 * @prop {boolean} [autoResize=false]
 * @prop {number}  [rows=4]      Initial visible rows.
 * @prop {number}  [minRows=2]   Lower bound when autoResize is on.
 * @prop {number}  [maxRows=12]  Upper bound when autoResize is on.
 * @prop {number}  [maxLength]
 * @prop {boolean} [showCount=false]
 * @prop {"none"|"vertical"|"both"} [resize="vertical"]
 * @prop {"sm"|"md"|"lg"} [size="md"]
 * @prop {"light"|"dark"} [tone="light"]   Tonal palette — matches PremiumInput.
 * @prop {"default"|"bare"} [variant="default"]
 *   "bare" removes all host chrome (border, ring, rounded shell, padding) —
 *   for hosting inside an already-styled glass shell such as the ChainChat
 *   composer bar. Sizing padding is still applied so the textarea breathes.
 * @prop {string} [name]
 * @prop {string} [autoComplete]
 * @prop {string} [testid]
 *   Yields `testid-wrap`, `testid`, `testid-error`, `testid-count`.
 * @prop {string} [className]
 * @prop {(e) => void} [onBlur]
 * @prop {(e) => void} [onFocus]
 * @prop {(e) => void} [onKeyDown]
 */
const PremiumTextareaInner = forwardRef(function PremiumTextareaInner({
  value,
  onChange,
  label,
  hint,
  error,
  placeholder,
  required = false,
  disabled = false,
  readOnly = false,
  loading = false,
  autoResize = false,
  rows = 4,
  minRows = 2,
  maxRows = 12,
  maxLength,
  showCount = false,
  resize = "vertical",
  size = "md",
  tone = "light",
  variant = "default",
  name,
  autoComplete,
  testid,
  className = "",
  onBlur,
  onFocus,
  onKeyDown,
  ...rest
}, externalRef) {
  const innerRef = useRef(null);
  const inputId = useId();
  const hintId = useId();
  const errorId = useId();
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (typeof externalRef === "function") externalRef(innerRef.current);
    else if (externalRef) externalRef.current = innerRef.current;
  }, [externalRef]);

  // Auto-resize: measure scrollHeight and clamp between minRows / maxRows.
  useLayoutEffect(() => {
    if (!autoResize) return;
    const el = innerRef.current;
    if (!el) return;
    const computed = window.getComputedStyle(el);
    const lineHeight = parseFloat(computed.lineHeight) || 20;
    const padY = parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom);
    const minH = lineHeight * minRows + padY;
    const maxH = lineHeight * maxRows + padY;
    // Reset to measure true scrollHeight, then clamp.
    el.style.height = "auto";
    const next = Math.max(minH, Math.min(maxH, el.scrollHeight));
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxH ? "auto" : "hidden";
  }, [value, autoResize, minRows, maxRows]);

  const hasError = !!error;
  const length = String(value ?? "").length;
  const isReadOnly = readOnly || loading;

  const sizing = {
    sm: { wrap: "px-2.5 py-2 text-[12.5px]",  ta: "" },
    md: { wrap: "px-3 py-2.5 text-[13.5px]",  ta: "" },
    lg: { wrap: "px-3.5 py-3 text-[14px]",    ta: "" },
  }[size] || { wrap: "px-3 py-2.5 text-[13.5px]", ta: "" };

  const handleChange = useCallback((e) => onChange?.(e.target.value), [onChange]);
  const handleFocus = useCallback((e) => { setFocused(true); onFocus?.(e); }, [onFocus]);
  const handleBlur = useCallback((e) => { setFocused(false); onBlur?.(e); }, [onBlur]);

  const effectiveResize = autoResize ? "none" : resize;
  const isDark = tone === "dark";
  const isBare = variant === "bare";

  // Tonal palettes — mirror PremiumInput so adjacent fields match.
  const labelTone        = isDark ? "text-slate-300"   : "text-slate-500";
  const placeholderTone  = isDark ? "placeholder-slate-500" : "placeholder-slate-400";
  const textTone         = isDark ? "text-slate-100"   : "text-slate-900";

  const countTone =
    !maxLength ? (isDark ? "text-slate-500" : "text-slate-400")
    : length >= maxLength ? "text-rose-500"
    : length >= maxLength * 0.9 ? "text-amber-500"
    : (isDark ? "text-slate-500" : "text-slate-400");

  return (
    <div className={`cf-ta ${className}`} data-testid={testid ? `${testid}-wrap` : undefined}>
      {label && (
        <label
          htmlFor={inputId}
          className={`block text-[10.5px] font-semibold uppercase tracking-[0.14em] mb-1.5 ${labelTone}`}
        >
          {label}
          {required && <span className="text-rose-500 ml-0.5" aria-hidden="true">*</span>}
        </label>
      )}
      <div
        className={
          `cf-ta-wrap relative ${sizing.wrap} ` +
          (isBare
            ? ""
            : ("rounded-xl border " +
              (isDark
                ? (hasError
                    ? "border-rose-400/50 bg-slate-900/40"
                    : "border-slate-700/60 bg-slate-900/30 hover:border-slate-600/80")
                : (hasError
                    ? "border-rose-300 bg-white"
                    : "border-slate-200 bg-white hover:border-slate-300")))) +
          (!isBare && focused
            ? hasError
              ? " ring-2 ring-rose-300/60 border-rose-300"
              : (isDark
                  ? " ring-2 ring-violet-400/40 border-slate-500/80"
                  : " ring-2 ring-violet-400/40 border-slate-300")
            : "") +
          (!isBare && disabled
            ? (isDark ? " opacity-60 cursor-not-allowed bg-slate-900/50" : " opacity-60 cursor-not-allowed bg-slate-50")
            : "") +
          (!isBare && isReadOnly && !disabled
            ? (isDark ? " bg-slate-900/40" : " bg-slate-50")
            : "")
        }
      >
        <textarea
          ref={innerRef}
          id={inputId}
          name={name}
          rows={rows}
          maxLength={maxLength}
          required={required}
          disabled={disabled}
          readOnly={isReadOnly}
          autoComplete={autoComplete}
          placeholder={placeholder}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? errorId : (hint ? hintId : undefined)}
          aria-required={required || undefined}
          value={value ?? ""}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          data-testid={testid}
          className={
            "block w-full bg-transparent outline-none border-none " +
            `${placeholderTone} ${textTone} leading-relaxed ` +
            (disabled ? "cursor-not-allowed " : "") +
            sizing.ta
          }
          style={{ resize: effectiveResize }}
          {...rest}
        />
      </div>
      {(hint || error || showCount) && (
        <div className="mt-1.5 flex items-baseline justify-between gap-3">
          <div className="flex-1 min-w-0">
            {(hint && !error) && (
              <p id={hintId} className="text-[11px] text-slate-500 leading-snug">
                {hint}
              </p>
            )}
            {error && (
              <p
                id={errorId}
                role="alert"
                data-testid={testid ? `${testid}-error` : undefined}
                className="text-[11px] text-rose-600 leading-snug"
              >
                {error}
              </p>
            )}
          </div>
          {showCount && (
            <span
              data-testid={testid ? `${testid}-count` : undefined}
              className={`text-[10.5px] tabular-nums ${countTone} flex-none`}
            >
              {length}{maxLength ? ` / ${maxLength}` : ""}
            </span>
          )}
        </div>
      )}
      <style>{`
        .cf-ta-wrap { transition: border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease; }
        @media (prefers-reduced-motion: reduce) {
          .cf-ta-wrap { transition: none !important; }
        }
      `}</style>
    </div>
  );
});

PremiumTextareaInner.displayName = "PremiumTextareaInner";

export const PremiumTextarea = memo(PremiumTextareaInner);
PremiumTextarea.displayName = "PremiumTextarea";

export default PremiumTextarea;
